import { useState } from "react";
import PropTypes from "prop-types";
import Card from "components/molecules/Card/Card";
import { FormattedMessage } from "react-intl";
import CardNew from "components/molecules/Card/CardNew";
import { ReactComponent as FileIcon } from "assets/images/icons/File.svg";
import { getInvoicePDF } from "api";
import { useGetInvoiceAttachments } from "api/queries/invoice/useGetInvoiceAttachments";

const initialAttachments = [];

const Files = function ({ invoiceId, isLoading, isInvoiceApproved }) {
  const [isLoadingPdfSummary, setIsLoadingPdfSummary] = useState(false);
  const { data: invoiceAttachments, isLoading: isLoadingAttachments } = useGetInvoiceAttachments({
    invoiceId,
    options: { enabled: !!invoiceId },
  });
  const bucketAttachments = invoiceAttachments?.bucket_attachments || initialAttachments;
  const otherAttachments = invoiceAttachments?.other_attachments || initialAttachments;
  const invoiceSummary = invoiceAttachments?.invoice_pdf;
  const stopAssociationEnabled = Boolean(invoiceAttachments?.stop_association_enabled);

  const downloadSummaryPDF = () => {
    setIsLoadingPdfSummary(true);
    getInvoicePDF(invoiceId)
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", `${invoiceId}-summary`);
        link.click();
      })
      .finally(() => setIsLoadingPdfSummary(false));
  };

  const renderStopContext = (attachment) => {
    if (!stopAssociationEnabled) return null;

    if (!attachment?.stop) {
      return (
        <span className="text-secondary text-xxs font-weight-normal">
          <FormattedMessage id="billing.invoiceAttachment.entireShipment" defaultMessage="Entire shipment" />
        </span>
      );
    }

    const address = attachment.stop.full_address;
    const hasSequence =
      attachment.stop.sequence !== null && attachment.stop.sequence !== undefined;

    return (
      <>
        {hasSequence && (
          <span className="text-secondary text-xxs font-weight-normal">
            <FormattedMessage
              id="billing.invoiceAttachment.stopLabel"
              defaultMessage="Stop {sequences}"
              values={{ sequences: attachment.stop.sequence }}
            />
          </span>
        )}
        {address && <span className="text-secondary text-xxs font-weight-normal">{address}</span>}
      </>
    );
  };

  return (
    <CardNew
      isLoading={isLoading || isLoadingAttachments}
      className="mb-15"
      title={<FormattedMessage id="tracking.components.files.files" defaultMessage="Files" />}
    >
      <Card.Body style={{ padding: 0 }} className="font-b6">
        {isInvoiceApproved && (
          <div className="d-flex align-items-center mb-1">
            <FileIcon className="mr-2" />
            <a
              href=""
              rel="noreferrer"
              onClick={(e) => {
                e.preventDefault();
                downloadSummaryPDF();
              }}
            >
              {invoiceId}-summary.pdf
            </a>
            {isLoadingPdfSummary && <div className="spinner-border spinner-border-sm ml-2" role="status" />}
          </div>
        )}
        {invoiceSummary && (
          <div className="d-flex align-items-center mb-1">
            <FileIcon className="mr-2" />
            <a
              target="_blank"
              href={invoiceSummary.url}
              rel="noreferrer"
            >
              {invoiceSummary.name}
            </a>
          </div>
        )}
        {bucketAttachments.map((attachment) => (
          <div
            key={`${attachment?.file?.id}-${attachment?.attachment_bucket?.id}-${attachment?.stop?.id || "no-stop"}`}
            className="d-flex align-items-start mb-1"
          >
            <FileIcon className="mr-2 mt-1" />
            <div className="d-flex flex-column">
              <a
                target="_blank"
                href={attachment.file.url}
                rel="noreferrer"
              >
                {attachment.attachment_bucket.bucket_name}.{attachment.file.file_extension}
              </a>
              {renderStopContext(attachment)}
            </div>
          </div>
        ))}
        {otherAttachments.map((attachment) => (
          <div key={attachment.id} className="d-flex align-items-center mb-1">
            <FileIcon className="mr-2" />
            <a
              target="_blank"
              href={attachment.url}
              rel="noreferrer"
            >
              {attachment.name}
            </a>
          </div>
        ))}
        {bucketAttachments.length === 0 && otherAttachments.length === 0 && !isInvoiceApproved && (
          <p className="text-secondary mb-0 text-center">
            <FormattedMessage id="billing.noInvoiceAttachmentsFor" defaultMessage="No invoice attachments for" /> {invoiceId}
          </p>
        )}
      </Card.Body>
    </CardNew>
  );
};

Files.propTypes = {
  invoiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLoading: PropTypes.bool.isRequired,
  isInvoiceApproved: PropTypes.bool.isRequired,
};

Files.defaultProps = {
  invoiceId: undefined,
};

export default Files;
